import React, { useState, useRef } from 'react';
import Papa from 'papaparse';
import Button from 'components/ui/Button';
import Icon from 'components/AppIcon';
import { productCatalogService } from 'services/productCatalogService';

const EXPECTED_COLUMNS = [
  'sku', 'product_type', 'category', 'subcategory', 'brand', 'name',
  'description', 'unit', 'sale_price', 'provider_name', 'supplier_sku',
  'cost_price', 'available_stock', 'lead_time_days',
  'make', 'model', 'year_from', 'year_to', 'engine', 'notes',
];

const CSVImportPanel = ({ onClose, onImportComplete }) => {
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [fileName, setFileName] = useState('');
  const [importing, setImporting] = useState(false);
  const [summary, setSummary] = useState(null);
  const [parseError, setParseError] = useState('');
  const fileRef = useRef();

  const parseFile = (file) => {
    setParseError('');
    setSummary(null);
    if (!file?.name?.endsWith('.csv')) {
      setParseError('Solo se aceptan archivos .csv');
      return;
    }
    setFileName(file?.name);
    Papa?.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        if (result?.errors?.length > 0) {
          setParseError(`Error al parsear: ${result?.errors?.[0]?.message}`);
          return;
        }
        setHeaders(result?.meta?.fields || []);
        setRows(result?.data || []);
      },
      error: (err) => setParseError(err?.message),
    });
  };

  const handleFileChange = (e) => {
    const file = e?.target?.files?.[0];
    if (file) parseFile(file);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setDragging(false);
    const file = e?.dataTransfer?.files?.[0];
    if (file) parseFile(file);
  };

  const handleImport = async () => {
    if (!rows?.length) return;
    setImporting(true);
    try {
      const result = await productCatalogService?.importFromCSV(rows);
      setSummary(result);
      onImportComplete?.();
    } catch (err) {
      setParseError(err?.message || 'Error durante la importación');
    } finally {
      setImporting(false);
    }
  };

  const previewRows = rows?.slice(0, 5);
  const previewHeaders = headers?.slice(0, 8);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-xl shadow-elevation-3 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-2">
            <Icon name="Upload" size={20} className="text-primary" />
            <h2 className="text-lg font-heading font-semibold text-foreground">Importar CSV</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted">
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Drop zone */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
              dragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
            }`}
            onDragOver={e => { e?.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileRef?.current?.click()}
          >
            <Icon name="FileText" size={36} className="mx-auto mb-3 text-muted-foreground" />
            {fileName ? (
              <p className="font-medium text-foreground">{fileName}</p>
            ) : (
              <>
                <p className="font-medium text-foreground">Arrastra tu archivo CSV aquí</p>
                <p className="text-sm text-muted-foreground mt-1">o haz clic para seleccionar</p>
              </>
            )}
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleFileChange} />
          </div>

          {parseError && (
            <div className="flex items-center gap-2 p-3 bg-error/10 border border-error/20 rounded-lg text-error text-sm">
              <Icon name="AlertCircle" size={16} />
              {parseError}
            </div>
          )}

          {/* Preview */}
          {rows?.length > 0 && !summary && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-foreground">
                  Vista previa — {rows?.length} filas detectadas (mostrando primeras 5)
                </p>
              </div>
              <div className="overflow-x-auto border border-border rounded-lg">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-muted/40 border-b border-border">
                      {previewHeaders?.map(h => (
                        <th key={h} className="text-left px-3 py-2 font-medium text-muted-foreground whitespace-nowrap">{h}</th>
                      ))}
                      {headers?.length > 8 && <th className="px-3 py-2 text-muted-foreground">+{headers?.length - 8} más</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows?.map((row, i) => (
                      <tr key={i} className="border-b border-border/50">
                        {previewHeaders?.map(h => (
                          <td key={h} className="px-3 py-2 text-foreground max-w-[120px] truncate">{row?.[h] || '—'}</td>
                        ))}
                        {headers?.length > 8 && <td className="px-3 py-2 text-muted-foreground">...</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Column hint */}
              <details className="mt-3">
                <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                  Ver columnas esperadas
                </summary>
                <p className="text-xs text-muted-foreground mt-1 font-mono">
                  {EXPECTED_COLUMNS?.join(', ')}
                </p>
              </details>
            </div>
          )}

          {/* Summary */}
          {summary && (
            <div className="p-4 bg-success/10 border border-success/20 rounded-lg space-y-2">
              <div className="flex items-center gap-2 text-success font-semibold">
                <Icon name="CheckCircle" size={18} />
                Importación completada
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Productos creados:</span>
                  <span className="font-medium">{summary?.productsCreated}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Productos actualizados:</span>
                  <span className="font-medium">{summary?.productsUpdated}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Proveedores creados:</span>
                  <span className="font-medium">{summary?.providersCreated}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Errores:</span>
                  <span className={`font-medium ${summary?.errors?.length > 0 ? 'text-error' : ''}`}>
                    {summary?.errors?.length}
                  </span>
                </div>
              </div>
              {summary?.errors?.length > 0 && (
                <details className="mt-2">
                  <summary className="text-xs text-error cursor-pointer">Ver errores</summary>
                  <ul className="mt-1 space-y-1">
                    {summary?.errors?.map((e, i) => (
                      <li key={i} className="text-xs text-error">{e}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onClose}>
              {summary ? 'Cerrar' : 'Cancelar'}
            </Button>
            {rows?.length > 0 && !summary && (
              <Button onClick={handleImport} loading={importing}>
                <Icon name="Upload" size={16} className="mr-2" />
                Confirmar importación ({rows?.length} filas)
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CSVImportPanel;
